/**
 * Return App Settings Component
 *
 * Form-based editor for merchant return app configuration.
 * Handles the flow_config JSONB structure with sections, questions, and options.
 */

import { useState, useEffect } from 'react';
import {
  Loader2,
  AlertCircle,
  AlertTriangle,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Camera,
  HelpCircle,
  CheckCircle2,
  Settings2,
} from 'lucide-react';
import {
  getMerchantConfiguration,
  createMerchantConfiguration,
  updateMerchantConfiguration,
  validateFlowConfig,
  formatValidationErrors,
  type MerchantConfiguration,
  type FlowConfig,
  type ValidationError,
} from '../services/merchant-configurations.service';
import { normalizeFlowConfig } from '../lib/flowConfigNormalization';
import {
  type Section,
  type PhotoUploadSection,
  type QuestionSetSection,
  type CompletionSection,
  type Question,
  type QuestionOption,
  type MultilingualText,
  type InstructionBox,
  DEFAULT_MINIMAL_CONFIG,
  createPhotoUploadSection,
  createBookingQuestionSetSection,
  createItemQuestionSetSection,
  createCompletionSection,
  createQuestion,
  createOption,
  createInstructionBox,
  VALID_QUESTION_TYPES,
  VALID_ACTIONS,
  VALID_PHOTO_MODES,
  VALID_PAGINATION_MODES,
  AVAILABLE_ICONS,
  AVAILABLE_COLORS,
} from '../types/merchant-configurations';
import { getMerchantById } from '../services/merchants.service';
import { getLocations } from '../services/locations.service';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_CONFIGS } from '../contexts/LanguageContext';

interface ReturnAppSettingsProps {
  merchantId: string;
  onSaveSuccess?: () => void;
  onSaveError?: (error: string) => void;
}

export default function ReturnAppSettings({
  merchantId,
  onSaveSuccess,
  onSaveError,
}: ReturnAppSettingsProps) {
  const { t } = useTranslation();
  const { merchantId: authMerchantId, locationIds: authLocationId, isPlatformAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configuration, setConfiguration] = useState<MerchantConfiguration | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<ValidationError[]>([]);

  // Form state
  const [autoCompleteBooking, setAutoCompleteBooking] = useState(true);
  const [allowReturnUpdates, setAllowReturnUpdates] = useState(true);
  const [sections, setSections] = useState<Section[]>([]);

  // UI state
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>(['en']);
  const [activeLanguage, setActiveLanguage] = useState<string>('en');

  // Locations available to the operator for the per-box visibility filter.
  // getLocations() applies the operator's access control, so a location-scoped
  // operator naturally sees only their own location(s) — and can therefore
  // only target those when assigning instruction boxes.
  const [merchantLocations, setMerchantLocations] = useState<{ id: string; location_name: string }[]>([]);

  useEffect(() => {
    loadConfiguration();
  }, [merchantId]);

  const loadConfiguration = async () => {
    setLoading(true);
    setError(null);

    // Load merchant's supported languages
    const { data: merchantData } = await getMerchantById(merchantId);
    if (merchantData?.supported_languages && Array.isArray(merchantData.supported_languages)) {
      const langs = merchantData.supported_languages as string[];
      setSupportedLanguages(langs.length > 0 ? langs : ['en']);
      setActiveLanguage(langs[0] || 'en');
    }

    // Load locations for the per-instruction-box visibility picker.
    // Pass the operator's access control so a location-scoped user can only
    // target their own location(s).
    const { data: locationsData } = await getLocations(
      { merchantId: authMerchantId, locationIds: authLocationId, isPlatformAdmin },
      { active: true }
    );
    if (locationsData) {
      setMerchantLocations(
        locationsData.map((l: any) => ({ id: l.id, location_name: l.location_name }))
      );
    }

    const { data, error } = await getMerchantConfiguration(merchantId);

    if (error) {
      setError(error);
      setLoading(false);
      return;
    }

    if (data) {
      setConfiguration(data);
      setAutoCompleteBooking(data.auto_complete_booking);
      setAllowReturnUpdates(data.allow_return_updates);
      setSections(data.flow_config.sections || []);
      // Expand all sections by default
      setExpandedSections(new Set(data.flow_config.sections.map(s => s.id)));
    }

    setLoading(false);
  };

  const handleCreateConfiguration = async () => {
    setSaving(true);
    setError(null);

    const { data, error } = await createMerchantConfiguration(
      merchantId,
      DEFAULT_MINIMAL_CONFIG,
      true,
      true
    );

    if (error) {
      setError(error);
      onSaveError?.(error);
      setSaving(false);
      return;
    }

    if (data) {
      setConfiguration(data);
      setAutoCompleteBooking(data.auto_complete_booking);
      setAllowReturnUpdates(data.allow_return_updates);
      setSections(data.flow_config.sections || []);
      setExpandedSections(new Set(data.flow_config.sections.map(s => s.id)));
      onSaveSuccess?.();
    }

    setSaving(false);
  };

  const handleSave = async () => {
    // Normalize before validating/persisting: strips empty-string language
    // keys, auto-fills "en" from the best available language, and cleans
    // instruction-box location_ids — so the saved config always satisfies
    // the return app's validation contract.
    const flowConfig: FlowConfig = normalizeFlowConfig({ sections }, supportedLanguages);

    // Validate the normalized config: errors block the save (the return app
    // would silently discard the config); warnings are shown but don't block.
    const { errors: validationErrors, warnings: validationWarnings } = validateFlowConfig(
      flowConfig,
      { merchantLanguages: supportedLanguages }
    );
    setWarnings(validationWarnings);
    if (validationErrors.length > 0) {
      const errorMessage = formatValidationErrors(validationErrors);
      setError(errorMessage);
      onSaveError?.(errorMessage);
      return;
    }

    setSaving(true);
    setError(null);

    const { data, error } = await updateMerchantConfiguration(merchantId, {
      flowConfig,
      autoCompleteBooking,
      allowReturnUpdates,
    });

    if (error) {
      setError(error);
      onSaveError?.(error);
      setSaving(false);
      return;
    }

    if (data) {
      setConfiguration(data);
      // Reflect the normalized (persisted) config in the editor so the
      // merchant sees exactly what was saved, including auto-filled languages.
      setSections(flowConfig.sections);
      setHasChanges(false);
      onSaveSuccess?.();
    }

    setSaving(false);
  };

  const markChanged = () => {
    setHasChanges(true);
  };

  // Section management
  const addSection = (type: 'photo_upload' | 'booking_questions' | 'item_questions' | 'completion') => {
    const maxOrder = sections.length > 0 ? Math.max(...sections.map(s => s.order)) : 0;
    let newSection: Section;

    switch (type) {
      case 'photo_upload':
        newSection = createPhotoUploadSection(maxOrder + 1, supportedLanguages);
        break;
      case 'booking_questions':
        newSection = createBookingQuestionSetSection(maxOrder + 1, supportedLanguages);
        break;
      case 'item_questions':
        newSection = createItemQuestionSetSection(maxOrder + 1, supportedLanguages);
        break;
      case 'completion':
        newSection = createCompletionSection(maxOrder + 1, supportedLanguages);
        break;
    }

    setSections([...sections, newSection]);
    setExpandedSections(prev => new Set([...prev, newSection.id]));
    markChanged();
  };

  const removeSection = (sectionId: string) => {
    setSections(sections.filter(s => s.id !== sectionId));
    markChanged();
  };

  const updateSection = (sectionId: string, updates: Partial<Section>) => {
    setSections(sections.map(s =>
      s.id === sectionId ? { ...s, ...updates } as Section : s
    ));
    markChanged();
  };

  const moveSection = (sectionId: string, direction: 'up' | 'down') => {
    const sortedSections = [...sections].sort((a, b) => a.order - b.order);
    const index = sortedSections.findIndex(s => s.id === sectionId);

    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === sortedSections.length - 1)
    ) {
      return;
    }

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const tempOrder = sortedSections[index].order;
    sortedSections[index] = { ...sortedSections[index], order: sortedSections[swapIndex].order };
    sortedSections[swapIndex] = { ...sortedSections[swapIndex], order: tempOrder };

    setSections(sortedSections);
    markChanged();
  };

  const toggleSectionExpanded = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-xl">
        <Loader2 className="w-8 h-8 animate-spin text-blue" />
      </div>
    );
  }

  // No configuration exists - show create button
  if (!configuration) {
    return (
      <div className="space-y-lg">
        <div className="text-center py-xl">
          <Settings2 className="w-16 h-16 text-secondary mx-auto mb-md" />
          <h3 className="text-lg font-semibold text-primary mb-sm">{t('returnApp.noConfiguration')}</h3>
          <p className="text-secondary mb-lg max-w-md mx-auto">
            {t('returnApp.noConfigurationDesc')}
          </p>
          <button
            onClick={handleCreateConfiguration}
            disabled={saving}
            className="btn btn-primary inline-flex items-center gap-sm"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('returnApp.creating')}
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                {t('returnApp.createConfiguration')}
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="p-md bg-danger/10 border border-danger/20 rounded-lg flex items-start gap-sm">
            <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}
      </div>
    );
  }

  const sortedSections = [...sections].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-lg">
      {/* Error display */}
      {error && (
        <div className="p-md bg-danger/10 border border-danger/20 rounded-lg flex items-start gap-sm">
          <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-danger font-medium">{t('returnApp.validationError')}</p>
            <p className="text-sm text-danger whitespace-pre-wrap">{error}</p>
          </div>
        </div>
      )}

      {/* Non-blocking warnings (e.g. missing translations) */}
      {warnings.length > 0 && (
        <div className="p-md bg-warning/10 border border-warning/30 rounded-lg flex items-start gap-sm">
          <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-warning font-medium">{t('returnApp.validationWarnings')}</p>
            <ul className="text-sm text-warning list-disc pl-md">
              {warnings.map((w, i) => (
                <li key={i}>{w.message} ({w.path})</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* General Settings */}
      <div>
        <h3 className="text-base font-semibold text-primary mb-md">{t('returnApp.generalSettings')}</h3>
        <div className="space-y-md">
          <label className="flex items-center gap-md cursor-pointer">
            <input
              type="checkbox"
              checked={autoCompleteBooking}
              onChange={(e) => {
                setAutoCompleteBooking(e.target.checked);
                markChanged();
              }}
              className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
            />
            <div>
              <span className="text-sm font-medium text-primary block">{t('returnApp.autoCompleteBooking')}</span>
              <span className="text-xs text-secondary">{t('returnApp.autoCompleteBookingDesc')}</span>
            </div>
          </label>
          <label className="flex items-center gap-md cursor-pointer">
            <input
              type="checkbox"
              checked={allowReturnUpdates}
              onChange={(e) => {
                setAllowReturnUpdates(e.target.checked);
                markChanged();
              }}
              className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
            />
            <div>
              <span className="text-sm font-medium text-primary block">{t('returnApp.allowReturnUpdates')}</span>
              <span className="text-xs text-secondary">{t('returnApp.allowReturnUpdatesDesc')}</span>
            </div>
          </label>
        </div>
      </div>

      {/* Language Toggle */}
      <div className="border-t border-primary pt-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-sm mb-md">
          <h3 className="text-base font-semibold text-primary">{t('returnApp.returnFlowSections')}</h3>
          <div className="flex items-center gap-sm">
            <span className="text-xs text-secondary">{t('returnApp.editLanguage')}</span>
            <div className="flex rounded-lg overflow-hidden border border-primary">
              {supportedLanguages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => setActiveLanguage(lang)}
                  className={`px-sm sm:px-md py-xs text-sm font-medium transition-colors ${
                    activeLanguage === lang
                      ? 'bg-blue text-white'
                      : 'bg-secondary text-secondary hover:bg-primary/10'
                  }`}
                >
                  {LANGUAGE_CONFIGS[lang]?.label || lang.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sections List */}
        <div className="space-y-md">
          {sortedSections.map((section, index) => (
            <SectionEditor
              key={section.id}
              section={section}
              isFirst={index === 0}
              isLast={index === sortedSections.length - 1}
              isExpanded={expandedSections.has(section.id)}
              activeLanguage={activeLanguage}
              supportedLanguages={supportedLanguages}
              merchantLocations={merchantLocations}
              onToggleExpanded={() => toggleSectionExpanded(section.id)}
              onUpdate={(updates) => updateSection(section.id, updates)}
              onRemove={() => removeSection(section.id)}
              onMoveUp={() => moveSection(section.id, 'up')}
              onMoveDown={() => moveSection(section.id, 'down')}
            />
          ))}
        </div>

        {/* Add Section Buttons */}
        <div className="mt-lg">
          <p className="text-sm text-secondary mb-sm">{t('returnApp.addSection')}</p>
          <div className="flex flex-wrap gap-sm">
            <button
              onClick={() => addSection('photo_upload')}
              className="btn btn-secondary text-sm inline-flex items-center gap-xs"
            >
              <Camera className="w-4 h-4" />
              {t('returnApp.photoUpload')}
            </button>
            <button
              onClick={() => addSection('booking_questions')}
              className="btn btn-secondary text-sm inline-flex items-center gap-xs"
            >
              <HelpCircle className="w-4 h-4" />
              {t('returnApp.bookingQuestions')}
            </button>
            <button
              onClick={() => addSection('item_questions')}
              className="btn btn-secondary text-sm inline-flex items-center gap-xs"
            >
              <HelpCircle className="w-4 h-4" />
              {t('returnApp.itemQuestions')}
            </button>
            <button
              onClick={() => addSection('completion')}
              className="btn btn-secondary text-sm inline-flex items-center gap-xs"
            >
              <CheckCircle2 className="w-4 h-4" />
              {t('returnApp.completion')}
            </button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="border-t border-primary pt-lg flex items-center justify-between">
        <div className="text-sm text-secondary">
          {hasChanges ? (
            <span className="text-warning">{t('returnApp.unsavedChanges')}</span>
          ) : (
            <span>{t('returnApp.allChangesSaved')}</span>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="btn btn-primary inline-flex items-center gap-sm"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('common.saving')}
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4" />
              {t('returnApp.saveConfiguration')}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Section Editor Component
interface SectionEditorProps {
  section: Section;
  isFirst: boolean;
  isLast: boolean;
  isExpanded: boolean;
  activeLanguage: string;
  supportedLanguages: string[];
  merchantLocations: { id: string; location_name: string }[];
  onToggleExpanded: () => void;
  onUpdate: (updates: Partial<Section>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function SectionEditor({
  section,
  isFirst,
  isLast,
  isExpanded,
  activeLanguage,
  supportedLanguages,
  merchantLocations,
  onToggleExpanded,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
}: SectionEditorProps) {
  const { t } = useTranslation();
  const getSectionIcon = () => {
    switch (section.type) {
      case 'photo_upload':
        return <Camera className="w-4 h-4" />;
      case 'question_set':
        return <HelpCircle className="w-4 h-4" />;
      case 'completion':
        return <CheckCircle2 className="w-4 h-4" />;
    }
  };

  const getSectionTitle = () => {
    switch (section.type) {
      case 'photo_upload':
        return t('returnApp.photoUpload');
      case 'question_set':
        const config = section.config as any;
        return config.scope === 'item' ? t('returnApp.itemQuestions') : t('returnApp.bookingQuestions');
      case 'completion':
        return t('returnApp.completion');
    }
  };

  return (
    <div className={`border rounded-lg ${section.enabled ? 'border-primary' : 'border-primary/50 bg-secondary/5'}`}>
      {/* Header */}
      <div
        className="flex items-center gap-sm p-md cursor-pointer"
        onClick={onToggleExpanded}
      >
        <div className="flex items-center gap-xs text-secondary flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
            disabled={isFirst}
            className="p-xs hover:bg-secondary rounded disabled:opacity-30"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
            disabled={isLast}
            className="p-xs hover:bg-secondary rounded disabled:opacity-30"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>

        <div className={`p-xs rounded flex-shrink-0 ${section.enabled ? 'bg-blue/10 text-blue' : 'bg-secondary text-secondary'}`}>
          {getSectionIcon()}
        </div>

        <div className="flex-1 min-w-0">
          <span className={`text-sm font-medium truncate block ${section.enabled ? 'text-primary' : 'text-secondary'}`}>
            {getSectionTitle()}
          </span>
        </div>

        <div className="flex items-center gap-sm flex-shrink-0">
          <label
            className="flex items-center gap-xs cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={section.enabled}
              onChange={(e) => onUpdate({ enabled: e.target.checked })}
              className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
            />
            <span className="text-xs text-secondary">{t('returnApp.enabled')}</span>
          </label>

          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="p-xs text-danger hover:bg-danger/10 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>

          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-secondary" />
          ) : (
            <ChevronDown className="w-5 h-5 text-secondary" />
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="border-t border-primary p-md">
          {section.type === 'photo_upload' && (
            <PhotoUploadSectionEditor
              section={section as PhotoUploadSection}
              activeLanguage={activeLanguage}
              supportedLanguages={supportedLanguages}
              onUpdate={onUpdate}
            />
          )}
          {section.type === 'question_set' && (
            <QuestionSetSectionEditor
              section={section as QuestionSetSection}
              activeLanguage={activeLanguage}
              supportedLanguages={supportedLanguages}
              onUpdate={onUpdate}
            />
          )}
          {section.type === 'completion' && (
            <CompletionSectionEditor
              section={section as CompletionSection}
              activeLanguage={activeLanguage}
              supportedLanguages={supportedLanguages}
              merchantLocations={merchantLocations}
              onUpdate={onUpdate}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Multilingual Text Input Component
interface MultilingualTextInputProps {
  label: string;
  value: MultilingualText | undefined;
  activeLanguage: string;
  supportedLanguages: string[];
  onChange: (value: MultilingualText) => void;
  multiline?: boolean;
  placeholder?: string;
}

function MultilingualTextInput({
  label,
  value,
  activeLanguage,
  supportedLanguages,
  onChange,
  multiline = false,
  placeholder,
}: MultilingualTextInputProps) {
  const currentValue = value?.[activeLanguage] || '';

  // Get other languages that have values
  const otherLangsWithValues = supportedLanguages
    .filter(lang => lang !== activeLanguage && value?.[lang])
    .slice(0, 2); // Show max 2 other languages

  const handleChange = (newValue: string) => {
    // Only touch the active language — never seed empty-string keys for the
    // other languages (the save-path normalizer strips any that remain from
    // previously saved configs).
    onChange({ ...value, [activeLanguage]: newValue });
  };

  return (
    <div>
      <label className="block text-sm font-medium text-primary mb-xs">
        {label} ({activeLanguage.toUpperCase()})
      </label>
      {multiline ? (
        <textarea
          value={currentValue}
          onChange={(e) => handleChange(e.target.value)}
          className="input w-full text-sm"
          rows={3}
          placeholder={placeholder}
        />
      ) : (
        <input
          type="text"
          value={currentValue}
          onChange={(e) => handleChange(e.target.value)}
          className="input w-full text-sm"
          placeholder={placeholder}
        />
      )}
      {otherLangsWithValues.length > 0 && (
        <p className="text-xs text-secondary mt-xs">
          {otherLangsWithValues.map(lang => `${LANGUAGE_CONFIGS[lang]?.label || lang.toUpperCase()}: ${value?.[lang] || ''}`).join(' | ')}
        </p>
      )}
    </div>
  );
}

// Photo Upload Section Editor
interface PhotoUploadSectionEditorProps {
  section: PhotoUploadSection;
  activeLanguage: string;
  supportedLanguages: string[];
  onUpdate: (updates: Partial<PhotoUploadSection>) => void;
}

function PhotoUploadSectionEditor({
  section,
  activeLanguage,
  supportedLanguages,
  onUpdate,
}: PhotoUploadSectionEditorProps) {
  const { t } = useTranslation();
  const config = section.config;

  const updateConfig = (updates: Partial<typeof config>) => {
    onUpdate({ config: { ...config, ...updates } });
  };

  return (
    <div className="space-y-md">
      <MultilingualTextInput
        label={t('returnApp.headerText')}
        value={config.header_text}
        activeLanguage={activeLanguage}
        supportedLanguages={supportedLanguages}
        onChange={(value) => updateConfig({ header_text: value })}
        placeholder="e.g., Upload Photos"
      />

      <MultilingualTextInput
        label={t('returnApp.instructions')}
        value={config.instructions}
        activeLanguage={activeLanguage}
        supportedLanguages={supportedLanguages}
        onChange={(value) => updateConfig({ instructions: value })}
        multiline
        placeholder="e.g., Please upload photos of the returned item"
      />

      <label className="flex items-center gap-sm cursor-pointer">
        <input
          type="checkbox"
          checked={config.show_instructions ?? true}
          onChange={(e) => updateConfig({ show_instructions: e.target.checked })}
          className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
        />
        <span className="text-sm text-primary">{t('returnApp.showInstructions')}</span>
      </label>

      <div className="grid grid-cols-2 gap-md">
        <div>
          <label className="block text-sm font-medium text-primary mb-xs">
            {t('returnApp.minPhotos')}
          </label>
          <input
            type="number"
            min="0"
            max="50"
            value={config.min_photos ?? 0}
            onChange={(e) => updateConfig({ min_photos: parseInt(e.target.value) || 0 })}
            className="input w-full text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-primary mb-xs">
            {t('returnApp.maxPhotos')}
          </label>
          <input
            type="number"
            min="1"
            max="50"
            value={config.max_photos ?? 10}
            onChange={(e) => updateConfig({ max_photos: parseInt(e.target.value) || 10 })}
            className="input w-full text-sm"
          />
        </div>
      </div>
    </div>
  );
}

// Question Set Section Editor
interface QuestionSetSectionEditorProps {
  section: QuestionSetSection;
  activeLanguage: string;
  supportedLanguages: string[];
  onUpdate: (updates: Partial<QuestionSetSection>) => void;
}

function QuestionSetSectionEditor({
  section,
  activeLanguage,
  supportedLanguages,
  onUpdate,
}: QuestionSetSectionEditorProps) {
  const { t } = useTranslation();
  const config = section.config;
  const isItemScope = config.scope === 'item';

  const updateConfig = (updates: Partial<typeof config>) => {
    onUpdate({ config: { ...config, ...updates } });
  };

  const addQuestion = () => {
    const maxOrder = config.questions.length > 0
      ? Math.max(...config.questions.map(q => q.order))
      : 0;
    updateConfig({
      questions: [...config.questions, createQuestion(maxOrder + 1, supportedLanguages)],
    });
  };

  const updateQuestion = (questionId: string, updates: Partial<Question>) => {
    updateConfig({
      questions: config.questions.map(q =>
        q.id === questionId ? { ...q, ...updates } : q
      ),
    });
  };

  const removeQuestion = (questionId: string) => {
    updateConfig({
      questions: config.questions.filter(q => q.id !== questionId),
    });
  };

  return (
    <div className="space-y-md">
      <MultilingualTextInput
        label={t('returnApp.headerText')}
        value={config.header_text}
        activeLanguage={activeLanguage}
        supportedLanguages={supportedLanguages}
        onChange={(value) => updateConfig({ header_text: value })}
        placeholder={isItemScope ? "e.g., Condition Check: {item_name}" : "e.g., General Questions"}
      />

      <MultilingualTextInput
        label={t('returnApp.instructions')}
        value={config.instructions}
        activeLanguage={activeLanguage}
        supportedLanguages={supportedLanguages}
        onChange={(value) => updateConfig({ instructions: value })}
        multiline
        placeholder="e.g., Please answer the following questions"
      />

      <label className="flex items-center gap-sm cursor-pointer">
        <input
          type="checkbox"
          checked={config.show_instructions ?? true}
          onChange={(e) => updateConfig({ show_instructions: e.target.checked })}
          className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
        />
        <span className="text-sm text-primary">{t('returnApp.showInstructions')}</span>
      </label>

      {/* Item-scope specific settings */}
      {isItemScope && (
        <div className="p-md bg-secondary/5 rounded-lg space-y-md">
          <h4 className="text-sm font-medium text-primary">{t('returnApp.itemScopeSettings')}</h4>

          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {t('returnApp.paginationMode')}
            </label>
            <select
              value={config.pagination || 'one_per_page'}
              onChange={(e) => updateConfig({ pagination: e.target.value as any })}
              className="input w-full text-sm"
            >
              {VALID_PAGINATION_MODES.map(mode => (
                <option key={mode} value={mode}>
                  {mode.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-md">
            <label className="flex items-center gap-sm cursor-pointer">
              <input
                type="checkbox"
                checked={config.skip_if_no_items ?? true}
                onChange={(e) => updateConfig({ skip_if_no_items: e.target.checked })}
                className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
              />
              <span className="text-sm text-primary">{t('returnApp.skipIfNoItems')}</span>
            </label>
            <label className="flex items-center gap-sm cursor-pointer">
              <input
                type="checkbox"
                checked={config.show_item_count ?? true}
                onChange={(e) => updateConfig({ show_item_count: e.target.checked })}
                className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
              />
              <span className="text-sm text-primary">{t('returnApp.showItemCount')}</span>
            </label>
          </div>

          {/* Item Photos Config */}
          <div className="border-t border-primary pt-md">
            <label className="flex items-center gap-sm cursor-pointer mb-md">
              <input
                type="checkbox"
                checked={config.item_photos_config?.enabled ?? false}
                onChange={(e) => updateConfig({
                  item_photos_config: {
                    ...config.item_photos_config,
                    enabled: e.target.checked,
                    mode: config.item_photos_config?.mode || 'optional',
                    min_photos: config.item_photos_config?.min_photos || 0,
                    max_photos: config.item_photos_config?.max_photos || 5,
                  },
                })}
                className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
              />
              <span className="text-sm font-medium text-primary">{t('returnApp.enablePerItemPhotos')}</span>
            </label>

            {config.item_photos_config?.enabled && (
              <div className="space-y-md pl-md">
                <div>
                  <label className="block text-sm font-medium text-primary mb-xs">
                    {t('returnApp.photoMode')}
                  </label>
                  <select
                    value={config.item_photos_config.mode}
                    onChange={(e) => updateConfig({
                      item_photos_config: {
                        ...config.item_photos_config!,
                        mode: e.target.value as any,
                      },
                    })}
                    className="input w-full text-sm"
                  >
                    {VALID_PHOTO_MODES.map(mode => (
                      <option key={mode} value={mode}>
                        {mode.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-md">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-xs">
                      {t('returnApp.minPhotos')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={config.item_photos_config.min_photos}
                      onChange={(e) => updateConfig({
                        item_photos_config: {
                          ...config.item_photos_config!,
                          min_photos: parseInt(e.target.value) || 0,
                        },
                      })}
                      className="input w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-xs">
                      {t('returnApp.maxPhotos')}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={config.item_photos_config.max_photos}
                      onChange={(e) => updateConfig({
                        item_photos_config: {
                          ...config.item_photos_config!,
                          max_photos: parseInt(e.target.value) || 5,
                        },
                      })}
                      className="input w-full text-sm"
                    />
                  </div>
                </div>

                <MultilingualTextInput
                  label={t('returnApp.photoInstructions')}
                  value={config.item_photos_config.instructions}
                  activeLanguage={activeLanguage}
                  supportedLanguages={supportedLanguages}
                  onChange={(value) => updateConfig({
                    item_photos_config: {
                      ...config.item_photos_config!,
                      instructions: value,
                    },
                  })}
                  placeholder="e.g., Upload photo of {item_number}"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Questions */}
      <div className="border-t border-primary pt-md">
        <div className="flex items-center justify-between mb-md">
          <h4 className="text-sm font-medium text-primary">
            {t('returnApp.questions')} ({config.questions.length}/5)
          </h4>
          <button
            onClick={addQuestion}
            disabled={config.questions.length >= 5}
            className="btn btn-secondary text-xs inline-flex items-center gap-xs disabled:opacity-50"
          >
            <Plus className="w-3 h-3" />
            {t('returnApp.addQuestion')}
          </button>
        </div>

        <div className="space-y-md">
          {config.questions.map((question, index) => (
            <QuestionEditor
              key={question.id}
              question={question}
              index={index}
              activeLanguage={activeLanguage}
              supportedLanguages={supportedLanguages}
              onUpdate={(updates) => updateQuestion(question.id, updates)}
              onRemove={() => removeQuestion(question.id)}
            />
          ))}

          {config.questions.length === 0 && (
            <p className="text-sm text-secondary text-center py-md">
              {t('returnApp.noQuestions')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Question Editor
interface QuestionEditorProps {
  question: Question;
  index: number;
  activeLanguage: string;
  supportedLanguages: string[];
  onUpdate: (updates: Partial<Question>) => void;
  onRemove: () => void;
}

function QuestionEditor({
  question,
  index,
  activeLanguage,
  supportedLanguages,
  onUpdate,
  onRemove,
}: QuestionEditorProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);

  const addOption = () => {
    onUpdate({
      options: [...(question.options || []), createOption(supportedLanguages)],
    });
  };

  const updateOption = (optionIndex: number, updates: Partial<QuestionOption>) => {
    const newOptions = [...(question.options || [])];
    newOptions[optionIndex] = { ...newOptions[optionIndex], ...updates };
    onUpdate({ options: newOptions });
  };

  const removeOption = (optionIndex: number) => {
    onUpdate({
      options: (question.options || []).filter((_, i) => i !== optionIndex),
    });
  };

  return (
    <div className="border border-primary/50 rounded-lg p-md">
      <div className="flex items-center justify-between mb-md">
        <div className="flex items-center gap-sm">
          <span className="text-xs font-medium text-secondary bg-secondary px-sm py-xs rounded">
            Q{index + 1}
          </span>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-secondary hover:text-primary"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
        <button
          onClick={onRemove}
          className="p-xs text-danger hover:bg-danger/10 rounded"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {isExpanded && (
        <div className="space-y-md">
          <MultilingualTextInput
            label={t('returnApp.questionText')}
            value={question.text}
            activeLanguage={activeLanguage}
            supportedLanguages={supportedLanguages}
            onChange={(value) => onUpdate({ text: value })}
            placeholder={t('returnApp.questionPlaceholder')}
          />

          <MultilingualTextInput
            label={t('returnApp.preamble')}
            value={question.preamble || undefined}
            activeLanguage={activeLanguage}
            supportedLanguages={supportedLanguages}
            onChange={(value) => onUpdate({ preamble: value })}
            multiline
            placeholder={t('returnApp.preamblePlaceholder')}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-md">
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('returnApp.questionType')}
              </label>
              <select
                value={question.type}
                onChange={(e) => onUpdate({ type: e.target.value as any })}
                className="input w-full text-sm"
              >
                {VALID_QUESTION_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={question.required}
                  onChange={(e) => onUpdate({ required: e.target.checked })}
                  className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
                />
                <span className="text-sm text-primary">{t('returnApp.required')}</span>
              </label>
            </div>
          </div>

          {/* Options (for radio/checkbox) */}
          {(question.type === 'radio' || question.type === 'checkbox') && (
            <div className="border-t border-primary pt-md">
              <div className="flex items-center justify-between mb-sm">
                <label className="text-sm font-medium text-primary">{t('returnApp.options')}</label>
                <button
                  onClick={addOption}
                  className="text-blue hover:text-blue/80 text-xs font-medium inline-flex items-center gap-xs"
                >
                  <Plus className="w-3 h-3" />
                  {t('returnApp.addOption')}
                </button>
              </div>

              <div className="space-y-sm">
                {(question.options || []).map((option, optIndex) => (
                  <OptionEditor
                    key={optIndex}
                    option={option}
                    index={optIndex}
                    activeLanguage={activeLanguage}
                    onUpdate={(updates) => updateOption(optIndex, updates)}
                    onRemove={() => removeOption(optIndex)}
                  />
                ))}

                {(!question.options || question.options.length === 0) && (
                  <p className="text-xs text-secondary text-center py-sm">
                    {t('returnApp.addAtLeastOneOption')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Option Editor
interface OptionEditorProps {
  option: QuestionOption;
  index: number;
  activeLanguage: string;
  onUpdate: (updates: Partial<QuestionOption>) => void;
  onRemove: () => void;
}

function OptionEditor({
  option,
  activeLanguage,
  onUpdate,
  onRemove,
}: OptionEditorProps) {
  const { t } = useTranslation();
  return (
    <div className="p-sm bg-secondary/5 rounded-lg">
      <div className="flex items-start gap-sm">
        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-sm">
          <div>
            <label className="block text-xs font-medium text-secondary mb-xs">{t('returnApp.value')}</label>
            <input
              type="text"
              value={option.value}
              onChange={(e) => onUpdate({ value: e.target.value })}
              className="input w-full text-xs"
              placeholder="yes/no/etc"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary mb-xs">
              {t('returnApp.label')} ({activeLanguage.toUpperCase()})
            </label>
            <input
              type="text"
              value={option.label?.[activeLanguage] || ''}
              onChange={(e) => {
                onUpdate({ label: { ...option.label, [activeLanguage]: e.target.value } });
              }}
              className="input w-full text-xs"
              placeholder={t('returnApp.displayLabel')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary mb-xs">{t('returnApp.action')}</label>
            <select
              value={option.action}
              onChange={(e) => onUpdate({ action: e.target.value as any })}
              className="input w-full text-xs"
            >
              {VALID_ACTIONS.map(action => (
                <option key={action} value={action}>{action.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={onRemove}
          className="p-xs text-danger hover:bg-danger/10 rounded mt-4"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {/* Blocking message (if action is block) */}
      {option.action === 'block' && (
        <div className="mt-sm">
          <label className="block text-xs font-medium text-secondary mb-xs">
            {t('returnApp.blockingMessage')} ({activeLanguage.toUpperCase()})
          </label>
          <input
            type="text"
            value={option.blocking_message?.[activeLanguage] || ''}
            onChange={(e) => {
              onUpdate({
                blocking_message: { ...option.blocking_message, [activeLanguage]: e.target.value },
              });
            }}
            className="input w-full text-xs"
            placeholder={t('returnApp.blockingMessagePlaceholder')}
          />
        </div>
      )}

      {/* Details config (if action is require_details) */}
      {option.action === 'require_details' && (
        <div className="mt-sm grid grid-cols-1 sm:grid-cols-2 gap-sm">
          <div>
            <label className="block text-xs font-medium text-secondary mb-xs">{t('returnApp.fieldName')}</label>
            <input
              type="text"
              value={option.details_config?.field_name || ''}
              onChange={(e) => onUpdate({
                details_config: {
                  ...option.details_config,
                  field_name: e.target.value,
                  required: option.details_config?.required ?? true,
                },
              })}
              className="input w-full text-xs"
              placeholder="e.g., damage_details"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary mb-xs">
              {t('returnApp.placeholder')} ({activeLanguage.toUpperCase()})
            </label>
            <input
              type="text"
              value={option.details_config?.placeholder?.[activeLanguage] || ''}
              onChange={(e) => {
                onUpdate({
                  details_config: {
                    ...option.details_config,
                    field_name: option.details_config?.field_name || '',
                    placeholder: {
                      ...option.details_config?.placeholder,
                      [activeLanguage]: e.target.value,
                    },
                    required: option.details_config?.required ?? true,
                  },
                });
              }}
              className="input w-full text-xs"
              placeholder={t('returnApp.placeholderText')}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Completion Section Editor
interface CompletionSectionEditorProps {
  section: CompletionSection;
  activeLanguage: string;
  supportedLanguages: string[];
  merchantLocations: { id: string; location_name: string }[];
  onUpdate: (updates: Partial<CompletionSection>) => void;
}

function CompletionSectionEditor({
  section,
  activeLanguage,
  supportedLanguages,
  merchantLocations,
  onUpdate,
}: CompletionSectionEditorProps) {
  const { t } = useTranslation();
  const config = section.config;

  // Soft warning: when at least one box uses a location filter, every visible
  // location should be covered by some box. If all boxes are scoped and one of
  // the operator's locations isn't in any box's list, that location's
  // confirmation page will be empty. Surface the names so the merchant can
  // decide whether that's intended.
  const uncoveredLocationNames: string[] = (() => {
    const boxes = config.instruction_boxes;
    if (boxes.length === 0 || merchantLocations.length === 0) return [];
    const allBoxesScoped = boxes.every(
      (b) => Array.isArray(b.location_ids) && b.location_ids.length > 0
    );
    if (!allBoxesScoped) return [];
    const covered = new Set(boxes.flatMap((b) => b.location_ids ?? []));
    return merchantLocations
      .filter((l) => !covered.has(l.id))
      .map((l) => l.location_name);
  })();

  const updateConfig = (updates: Partial<typeof config>) => {
    onUpdate({ config: { ...config, ...updates } });
  };

  const addInstructionBox = () => {
    const maxOrder = config.instruction_boxes.length > 0
      ? Math.max(...config.instruction_boxes.map(b => b.order))
      : 0;
    updateConfig({
      instruction_boxes: [...config.instruction_boxes, createInstructionBox(maxOrder + 1, supportedLanguages)],
    });
  };

  const updateInstructionBox = (index: number, updates: Partial<InstructionBox>) => {
    const newBoxes = [...config.instruction_boxes];
    newBoxes[index] = { ...newBoxes[index], ...updates };
    updateConfig({ instruction_boxes: newBoxes });
  };

  const removeInstructionBox = (index: number) => {
    updateConfig({
      instruction_boxes: config.instruction_boxes.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-md">
      <MultilingualTextInput
        label={t('returnApp.headerText')}
        value={config.header_text}
        activeLanguage={activeLanguage}
        supportedLanguages={supportedLanguages}
        onChange={(value) => updateConfig({ header_text: value })}
        placeholder="e.g., Thank You!"
      />

      <MultilingualTextInput
        label={t('returnApp.successMessage')}
        value={config.message}
        activeLanguage={activeLanguage}
        supportedLanguages={supportedLanguages}
        onChange={(value) => updateConfig({ message: value })}
        multiline
        placeholder="e.g., Your return has been successfully recorded."
      />

      {/* Review Request */}
      <div className="border-t border-primary pt-md">
        <label className="flex items-center gap-sm cursor-pointer mb-md">
          <input
            type="checkbox"
            checked={config.review_request?.enabled ?? false}
            onChange={(e) => updateConfig({
              review_request: {
                ...config.review_request,
                enabled: e.target.checked,
              },
            })}
            className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
          />
          <span className="text-sm font-medium text-primary">{t('returnApp.enableReviewRequest')}</span>
        </label>

        {config.review_request?.enabled && (
          <div className="space-y-md pl-md">
            <MultilingualTextInput
              label={t('returnApp.reviewRequestText')}
              value={config.review_request.text}
              activeLanguage={activeLanguage}
              supportedLanguages={supportedLanguages}
              onChange={(value) => updateConfig({
                review_request: { ...config.review_request, text: value },
              })}
              placeholder="e.g., Did you enjoy your experience?"
            />

            <MultilingualTextInput
              label={t('returnApp.buttonText')}
              value={config.review_request.button_text}
              activeLanguage={activeLanguage}
              supportedLanguages={supportedLanguages}
              onChange={(value) => updateConfig({
                review_request: { ...config.review_request, button_text: value },
              })}
              placeholder="e.g., Leave a review on Google Maps"
            />

            <label className="flex items-center gap-sm cursor-pointer">
              <input
                type="checkbox"
                checked={config.review_request.use_location_specific_url ?? false}
                onChange={(e) => updateConfig({
                  review_request: {
                    ...config.review_request,
                    use_location_specific_url: e.target.checked,
                  },
                })}
                className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
              />
              <span className="text-sm text-primary">{t('returnApp.useLocationSpecificUrl')}</span>
            </label>
          </div>
        )}
      </div>

      {/* Instruction Boxes */}
      <div className="border-t border-primary pt-md">
        <div className="flex items-center justify-between mb-md">
          <h4 className="text-sm font-medium text-primary">
            {t('returnApp.instructionBoxes')} ({config.instruction_boxes.length}/3)
          </h4>
          <button
            onClick={addInstructionBox}
            disabled={config.instruction_boxes.length >= 3}
            className="btn btn-secondary text-xs inline-flex items-center gap-xs disabled:opacity-50"
          >
            <Plus className="w-3 h-3" />
            {t('returnApp.addBox')}
          </button>
        </div>

        <div className="space-y-md">
          {config.instruction_boxes.map((box, index) => (
            <div key={index} className="p-md bg-secondary/5 rounded-lg">
              <div className="flex items-center justify-between mb-sm">
                <span className="text-xs font-medium text-secondary">{t('returnApp.boxNumber', { number: index + 1 })}</span>
                <button
                  onClick={() => removeInstructionBox(index)}
                  className="p-xs text-danger hover:bg-danger/10 rounded"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-sm">
                <MultilingualTextInput
                  label={t('returnApp.title')}
                  value={box.title}
                  activeLanguage={activeLanguage}
                  supportedLanguages={supportedLanguages}
                  onChange={(value) => updateInstructionBox(index, { title: value })}
                  placeholder={t('returnApp.boxTitlePlaceholder')}
                />

                <MultilingualTextInput
                  label={t('returnApp.text')}
                  value={box.text}
                  activeLanguage={activeLanguage}
                  supportedLanguages={supportedLanguages}
                  onChange={(value) => updateInstructionBox(index, { text: value })}
                  multiline
                  placeholder={t('returnApp.boxContentPlaceholder')}
                />

                <div className="grid grid-cols-2 gap-sm">
                  <div>
                    <label className="block text-xs font-medium text-secondary mb-xs">{t('returnApp.icon')}</label>
                    <select
                      value={box.icon}
                      onChange={(e) => updateInstructionBox(index, { icon: e.target.value })}
                      className="input w-full text-xs"
                    >
                      {AVAILABLE_ICONS.map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-secondary mb-xs">{t('returnApp.color')}</label>
                    <select
                      value={box.color}
                      onChange={(e) => updateInstructionBox(index, { color: e.target.value })}
                      className="input w-full text-xs"
                    >
                      {AVAILABLE_COLORS.map(color => (
                        <option key={color} value={color}>{color}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Per-box location visibility filter */}
                {merchantLocations.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-secondary mb-xs">
                      {t('returnApp.boxShowOnLocations')}
                    </label>
                    <div className="flex flex-wrap gap-xs">
                      {merchantLocations.map((loc) => {
                        const selected = (box.location_ids ?? []).includes(loc.id);
                        return (
                          <label
                            key={loc.id}
                            className={`inline-flex items-center gap-xs text-xs px-sm py-xs rounded-md border cursor-pointer transition-colors ${
                              selected
                                ? 'border-blue bg-blue/10 text-blue'
                                : 'border-primary text-secondary hover:bg-secondary/10'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => {
                                const current = box.location_ids ?? [];
                                const next = e.target.checked
                                  ? Array.from(new Set([...current, loc.id]))
                                  : current.filter((id) => id !== loc.id);
                                // Pass the new array directly. Empty arrays stay
                                // in form state for now; the save serializer
                                // strips them so the persisted JSON stays clean.
                                updateInstructionBox(index, { location_ids: next });
                              }}
                              className="w-3 h-3 text-blue border-primary rounded focus:ring-blue"
                            />
                            <span>{loc.location_name}</span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-xs text-secondary mt-xs">
                      {t('returnApp.boxShowOnLocationsHint')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}

          {uncoveredLocationNames.length > 0 && (
            <div className="bg-warning/10 border border-warning/30 rounded-md p-sm flex items-start gap-xs">
              <AlertTriangle className="w-3.5 h-3.5 text-warning flex-shrink-0 mt-0.5" />
              <p className="text-xs text-warning">
                {t('returnApp.uncoveredLocationsWarning', {
                  locations: uncoveredLocationNames.join(', '),
                })}
              </p>
            </div>
          )}

          {config.instruction_boxes.length === 0 && (
            <p className="text-sm text-secondary text-center py-md">
              {t('returnApp.noInstructionBoxes')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
